import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { api, ApiError } from "../../shared/api/client";
import { Button, Drawer, InlineAlert, LoadingState, cn } from "../../shared/ui";
import type { EmployeeDetail, RoleView } from "../../shared/types/api";

/**
 * Replaces an employee's roles (§12).
 *
 * <p>A whole-set replacement rather than add and remove, matching the endpoint. Someone's access
 * should be decided as one deliberate statement; two interleaved partial edits could leave a
 * person holding a combination neither administrator chose.
 *
 * <p>The panel states plainly that a change takes effect at the person's next token refresh.
 * Permissions travel inside the access token, so revoking one does not sever an existing session
 * instantly — and an administrator removing access during an incident needs to know that.
 */
export function AssignRolesDrawer({
  employee,
  onClose,
  onSaved,
}: {
  employee: EmployeeDetail | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [selected, setSelected] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const roles = useQuery({
    queryKey: ["roles"],
    queryFn: () => api.get<RoleView[]>("/api/v1/roles"),
    enabled: Boolean(employee),
  });

  useEffect(() => {
    if (employee) {
      setSelected(employee.roles);
      setError(null);
    }
  }, [employee]);

  const toggle = (code: string) => {
    setSelected((current) =>
      current.includes(code) ? current.filter((value) => value !== code) : [...current, code],
    );
  };

  const submit = async () => {
    if (!employee) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.post(`/api/v1/employees/${employee.id}/roles`, { roles: selected });
      onSaved();
      onClose();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : "Could not change the roles.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={Boolean(employee)}
      onClose={onClose}
      title="Roles"
      subtitle={employee?.fullName}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} loading={saving}>
            {saving ? "Saving…" : "Save roles"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {error && <InlineAlert tone="error">{error}</InlineAlert>}

        <InlineAlert tone="info">
          A change takes effect the next time they refresh their session, within about fifteen
          minutes. To cut off access immediately, set their status to suspended or exited instead.
        </InlineAlert>

        {roles.isPending && <LoadingState label="Loading roles…" />}

        {roles.isSuccess && (
          <div className="space-y-2">
            {roles.data.map((role) => {
              const checked = selected.includes(role.code);
              return (
                <label
                  key={role.code}
                  className={cn(
                    "flex cursor-pointer items-start gap-3 rounded-lg border px-4 py-3 transition-colors",
                    checked
                      ? "border-brand-300 bg-brand-50"
                      : "border-ink-200 bg-surface hover:bg-ink-50",
                  )}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(role.code)}
                    className="mt-0.5 h-4 w-4 shrink-0 accent-brand-600"
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-ink-900">{role.name}</span>
                    {role.description && (
                      <span className="block text-sm text-ink-500">{role.description}</span>
                    )}
                    <span className="mt-0.5 block text-xs text-ink-400">
                      {role.permissions.length} permission
                      {role.permissions.length === 1 ? "" : "s"}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>
        )}

        {selected.length === 0 && (
          <InlineAlert tone="warning">
            With no roles they can sign in but will see almost nothing. Most people need at least
            EMPLOYEE.
          </InlineAlert>
        )}
      </div>
    </Drawer>
  );
}
